import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import { useSetRecoilState } from 'recoil';
import { useUpdateEffect, useWindowSize } from 'usehooks-ts';
import { TablePaginationConfig } from 'antd';
import { ColumnType } from 'antd/lib/table';

import { alertsSelectorAdd } from '../../../../recoil';
import { LARGE_PAGE_SIZE, LAYOUT_WIDTH_BREAKPOINTS } from '../../../../constants';
import { getPageFromSearchParams, PAGE_INDEX_PARAM, useQuery } from '../../../../utils/urlUtil';
import { AgentMessageModel, AgentModel, AgentRunModel, SortDirection } from '../../../../../gamma-api';
import { defaultApi } from '../../../../apis';
import { formatDateTimeIso, formatDateTimeShort, getTranslateFunc } from '../../../../utils/stringUtil';
import { getPayloadsTextContent } from '../../../../utils/messageUtil';
import IbTable from '../../../../components/common/IbTable';
import IbPagination from '../../../../components/common/IbPagination';
import ResizableDrawer from '../../../../components/common/ResizableDrawer';
import AgentTestingChat from '../../../../components/AgentTestingChat';
import FilterableDebounceInput, {
  FilterableDebounceInputParam,
  IFilterableDebounceInputValue,
} from '../../../../components/common/FilterableDebounceInput';

import './index.less';

interface IRunsTabProps {
  agent?: AgentModel;
}

const RUNS_PAGE_SIZE = 30;
const RUN_ID_PARAM = 'runId';

const RunsTab: React.FC<IRunsTabProps> = ({ agent }) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const { push } = useHistory();
  const query = useQuery();
  const addAlert = useSetRecoilState(alertsSelectorAdd);
  const windowSize = useWindowSize();
  const isDesktop = windowSize.width > LAYOUT_WIDTH_BREAKPOINTS.small;
  const translate = getTranslateFunc('Agent: Runs:');

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: getPageFromSearchParams(location.search),
    pageSize: RUNS_PAGE_SIZE,
    total: 0,
  });

  const [runList, setRunList] = useState<AgentRunModel[]>([]);
  const [runListLoading, setRunListLoading] = useState(false);
  const [selectedRun, setSelectedRun] = useState<AgentRunModel>();

  const [messageList, setMessageList] = useState<AgentMessageModel[]>([]);
  const [messageListLoading, setMessageListLoading] = useState(false);

  const [drawerVisible, setDrawerVisible] = useState(false);

  const [inputValue, setInputValue] = useState<IFilterableDebounceInputValue>({ searchText: '' });

  const loadRuns = async () => {
    if (!agent) return;
    try {
      setRunListLoading(true);
      const response = await defaultApi.searchAgentAllRuns(
        [agent.agent_id],
        null,
        inputValue.searchText,
        formatDateTimeIso(inputValue.filter?.startDate, inputValue.filter?.startTime),
        formatDateTimeIso(inputValue.filter?.endDate, inputValue.filter?.endTime),
        SortDirection.Desc,
        (pagination.current || 1) - 1,
        RUNS_PAGE_SIZE,
        true
      );
      setRunList(response.data.items || []);
      setPagination((prev) => ({ ...prev, total: response.data.total_item_count || 0 }));
    } catch (e) {
      addAlert({ content: translate('Failed to load runs'), type: 'error' });
    } finally {
      setRunListLoading(false);
    }
  };

  const loadRun = async (runId: string) => {
    if (!agent) return;
    try {
      const response = await defaultApi.getOneAgentRun(runId);
      setSelectedRun(response.data);
    } catch (e) {
      addAlert({ content: translate('Failed to load run'), type: 'error' });
    }
  };

  const fetchMessages = async () => {
    if (!selectedRun) return;
    try {
      setMessageListLoading(true);
      const response = await defaultApi.searchAgentAllMessages(
        null,
        null,
        [selectedRun.run_id],
        null,
        0,
        LARGE_PAGE_SIZE
      );
      setMessageList(response.data.items || []);
    } catch (err) {
      addAlert({ content: translate('Failed to load messages'), type: 'error' });
    } finally {
      setMessageListLoading(false);
    }
  };

  useEffect(() => {
    const runId = query.get(RUN_ID_PARAM);
    if (!runId) {
      setSelectedRun(undefined);
      setDrawerVisible(false);
    } else if (!selectedRun) {
      void loadRun(runId);
      setDrawerVisible(true);
    }
  }, [location]);

  useEffect(() => {
    void fetchMessages();
  }, [selectedRun]);

  useEffect(() => {
    void loadRuns();
  }, [pagination.current, pagination.pageSize, inputValue.searchText, inputValue.filter, agent]);

  useEffect(() => {
    const page = getPageFromSearchParams(location.search);
    if (page !== pagination.current) {
      setPagination((prev) => ({ ...prev, current: page }));
    }
  }, [location]);

  useUpdateEffect(() => {
    if (pagination.current !== 1) {
      setPagination({ ...pagination, current: 1 });
    }
  }, [inputValue.searchText, inputValue.filter]);

  useUpdateEffect(() => {
    if (pagination.current) {
      query.set(PAGE_INDEX_PARAM, pagination.current.toString());
      push({ pathname: location.pathname, search: query.toString() });
    }
  }, [pagination.current]);

  const renderDate = (item: AgentRunModel) => {
    return formatDateTimeShort(item.created_on, i18n.language);
  };

  const columns: ColumnType<AgentRunModel>[] = [
    {
      title: translate('Table: Input data'),
      render: (_, record) => {
        return getPayloadsTextContent(record.input);
      },
      width: isDesktop ? '33%' : undefined,
      ellipsis: true,
    },
    {
      title: translate('Table: Output data'),
      render: (_, record) => {
        return getPayloadsTextContent(record.output);
      },
      width: isDesktop ? '33%' : undefined,
      ellipsis: true,
    },
    {
      title: translate('Table: Status'),
      render: (_, record) => {
        return translate(`Table: Status: ${record.status}`);
      },
    },
    {
      title: translate('Table: Runs creation date'),
      render: renderDate,
    },
  ];

  const getRunRowKey = (run: AgentRunModel) => run.run_id;

  const onRunRowClick = (run: AgentRunModel) => {
    setSelectedRun(run);
    query.set(RUN_ID_PARAM, run.run_id);
    push({ pathname: location.pathname, search: query.toString() });
    setDrawerVisible(true);
  };

  const onPaginationChange = (page: number) => setPagination({ ...pagination, current: page });

  const onDrawerClose = () => {
    setSelectedRun(undefined);
    query.delete(RUN_ID_PARAM);
    push({ pathname: location.pathname, search: query.toString() });
    setDrawerVisible(false);
  };

  const drawerTitle = () => {
    if (!selectedRun) return;
    return <span>{`${translate('Message List: Title')} ${renderDate(selectedRun)}`}</span>;
  };

  return (
    <div className="runs-tab">
      <FilterableDebounceInput
        params={[FilterableDebounceInputParam.DateTime]}
        value={inputValue}
        onChange={setInputValue}
      />
      <IbTable
        columns={columns}
        dataSource={runList}
        loading={runListLoading}
        rowKey={getRunRowKey}
        onRowClick={onRunRowClick}
      />
      {!runListLoading && (pagination.total ?? 0) > RUNS_PAGE_SIZE && (
        <div className="runs-tab__pagination-row">
          <IbPagination
            current={pagination.current}
            defaultCurrent={1}
            defaultPageSize={RUNS_PAGE_SIZE}
            pageSize={RUNS_PAGE_SIZE}
            showSizeChanger={false}
            total={pagination.total}
            onChange={onPaginationChange}
          />
        </div>
      )}
      <ResizableDrawer
        resizable
        bodyStyle={{ padding: 0 }}
        title={drawerTitle()}
        visible={drawerVisible}
        width="75%"
        onClose={onDrawerClose}
      >
        <AgentTestingChat readonly agent={agent} loading={messageListLoading} messageList={messageList} />
      </ResizableDrawer>
    </div>
  );
};

export default RunsTab;
